
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { usePageMeta } from "@/hooks/usePageMeta";

const company = {
  name: "Center Valley Logistics Inc",
  city: "Bakersfield",
  state: "CA",
  creditScore: 90,
  daysToPay: 22,
  dot: "3250762",
  mc: "MC1022984",

  office: {
    companyName: "Center Valley Logistics Inc",
    parentAccount: "Yes",
    companyType: "Broker",
    address: ["3200 Larson Lane Ste 42106", "Bakersfield, CA 93304 US"],
    phone: "877-828-4900",
    fax: "—",
    affiliations: "tianet",
    source: "FMCSA",
  },

  general: {
    docket: "MC1022984",
    authorityName: "CENTER VALLEY LOGISTICS INC",
    dotNumber: "3250762",
    entityType: "BROKER",
    dbaName: "Unknown",
    operationType: "AUTHORIZED FOR Property",
    operatingStatus: "AUTHORIZED FOR Property",
    businessAddress: ["3200 LARSON LANE SUITE 42106", "BAKERSFIELD, CA 93304 US"],
    businessPhone: "877-828-4900",
    mailingAddress: ["3200 LARSON LANE SUITE 42106", "BAKERSFIELD, CA 93304 US"],
    outOfInterstate: "No",
    commodities: "—",
    specialCommodities: "—",
    source: "FMCSA",
  },

  dotAuthority: {
    asOf: "02/21/26",
    status: [
      { type: "Common", status: "None", pending: "No" },
      { type: "Contract", status: "None", pending: "No" },
      { type: "Broker", status: "Active", pending: "No" },
    ],
    requirements: [
      { type: "BIPD", required: "$0", onFile: "$0" },
      { type: "Cargo", required: "No", onFile: "No" },
      { type: "Bond", required: "Yes", onFile: "Yes" },
    ],
    activeInsurance: {
      policySurety: "84 / Bond",
      coverageFrom: "14900",
      coverageTo: "$75,000",
      carrier: "SOUTHWEST MARINE AND GENERAL INSURANCE COMPAN",
      contact: "GABRIELA TORO / AVALON RISK MANAGEMENT",
      address: ["150 NORTHWEST POINT BLVD., 2ND FLR", "ELK GROVE VILLAGE, IL 60007"],
      phone: "847-235-6283",
      fax: "847-890-6413",
      effectiveDate: "06/05/23",
      cancellationDate: "—",
    },
    source: "Center Valley Logistics Inc",
  },

  background: {
    primaryContact: "Aman",
    primaryTitle: "Corporation",
    corporationType: "Corporation",
    phone: "661-899-5400",
    email: "logistics@cvlgx.com",
    address: ["3200 Larson Lane Ste 42106", "Bakersfield, CA 93304 US"],
    yearFounded: "2019",
    scac: "CVLW",
    veteranOwned: "No",
    womanOwned: "No",
    minorityOwned: "Not Minority Owned",
    certifiedNMSDC: "—",
    otherLocations: "—",
    source: "Ansonia",
  },

  creditHistory: [
    { month: "Dec 24", score: 97, dtp: 20 },
    { month: "Jan 25", score: 97, dtp: 23 },
    { month: "Feb 25", score: 97, dtp: 20 },
    { month: "Mar 25", score: 97, dtp: 19 },
    { month: "Apr 25", score: 97, dtp: 19 },
    { month: "May 25", score: 97, dtp: 21 },
    { month: "Jun 25", score: 97, dtp: 21 },
    { month: "Jul 25", score: 95, dtp: 22 },
    { month: "Aug 25", score: 95, dtp: 21 },
    { month: "Sep 25", score: 95, dtp: 23 },
    { month: "Oct 25", score: 90, dtp: 29 },
    { month: "Nov 25", score: 90, dtp: 29 },
    { month: "Dec 25", score: 90, dtp: 28 },
  ],
};

function LabelRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 py-1.5 border-b border-border/40 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xs text-foreground">{children}</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground leading-none">
      {children}
    </span>
  );
}

function SourceFooter({ source }: { source: string }) {
  return (
    <p className="text-[10px] text-muted-foreground mt-3">
      Source: <span className="underline">{source}</span>
    </p>
  );
}

const chartConfig = {
  score: { label: "Credit Score", color: "hsl(var(--primary))" },
  dtp: { label: "Days to Pay", color: "hsl(var(--muted-foreground))" },
};

export default function MarketplacePageV2() {
  usePageMeta({ title: "Companies | LaneRunner", description: "Search carriers and brokers directory" });
  
  return (
    <>
      <div className="flex flex-col gap-2.5 xl:gap-3 w-full">
        <Card>
          <CardContent className="p-4">
            <SectionLabel>Summary</SectionLabel>
            <h1 className="text-xl font-semibold text-foreground mt-1">{company.name}</h1>
            <p className="text-sm text-muted-foreground">
              {company.city}, {company.state}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Badge variant="secondary">Credit: {company.creditScore}</Badge>
              <Badge variant="secondary">Days to Pay: {company.daysToPay}</Badge>
              <Badge variant="secondary">DOT: {company.dot}</Badge>
              <Badge variant="secondary">MC: {company.mc}</Badge>
              <Badge variant="outline" className="text-primary border-primary/30 bg-primary/10">
                AUTHORIZED FOR Property
              </Badge>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
          <div className="flex flex-col gap-3">
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <h2 className="text-sm font-semibold text-foreground">Office Information</h2>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <LabelRow label="Company name">{company.office.companyName}</LabelRow>
                <LabelRow label="Parent account">
                  {company.office.parentAccount}{" "}
                  <span className="text-[10px] text-primary underline cursor-pointer ml-1">See all offices</span>
                </LabelRow>
                <LabelRow label="Company type">{company.office.companyType}</LabelRow>
                <LabelRow label="Address">
                  {company.office.address.map((l, i) => (
                    <span key={i} className="block">{l}</span>
                  ))}
                </LabelRow>
                <LabelRow label="Office phone">{company.office.phone}</LabelRow>
                <LabelRow label="Office fax">{company.office.fax}</LabelRow>
                <LabelRow label="Affiliations">{company.office.affiliations}</LabelRow>
                <SourceFooter source={company.office.source} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <h2 className="text-sm font-semibold text-foreground">General Information</h2>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <LabelRow label="Docket">{company.general.docket}</LabelRow>
                <LabelRow label="Authority name">{company.general.authorityName}</LabelRow>
                <LabelRow label="DOT Number">{company.general.dotNumber}</LabelRow>
                <LabelRow label="Entity type">{company.general.entityType}</LabelRow>
                <LabelRow label="DBA name">{company.general.dbaName}</LabelRow>
                <LabelRow label="Operation type">{company.general.operationType}</LabelRow>
                <LabelRow label="Operating status">{company.general.operatingStatus}</LabelRow>
                <LabelRow label="Business address">
                  {company.general.businessAddress.map((l, i) => (
                    <span key={i} className="block">{l}</span>
                  ))}
                </LabelRow>
                <LabelRow label="Business phone">{company.general.businessPhone}</LabelRow>
                <LabelRow label="Mailing address">
                  {company.general.mailingAddress.map((l, i) => (
                    <span key={i} className="block">{l}</span>
                  ))}
                </LabelRow>
                <LabelRow label="Out of interstate">{company.general.outOfInterstate}</LabelRow>
                <LabelRow label="Commodities">{company.general.commodities}</LabelRow>
                <LabelRow label="Special commodities">{company.general.specialCommodities}</LabelRow>
                <SourceFooter source={company.general.source} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <h2 className="text-sm font-semibold text-foreground">DOT Authority & Insurance</h2>
                <p className="text-[10px] text-muted-foreground">as of {company.dotAuthority.asOf}</p>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-4">
                <div>
                  <p className="text-xs font-medium text-foreground mb-1">DOT Authority Status</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="h-8 text-xs">Type</TableHead>
                        <TableHead className="h-8 text-xs">Status</TableHead>
                        <TableHead className="h-8 text-xs">Pending</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {company.dotAuthority.status.map((r) => (
                        <TableRow key={r.type}>
                          <TableCell className="py-1.5 text-xs">{r.type}</TableCell>
                          <TableCell className="py-1.5 text-xs">{r.status}</TableCell>
                          <TableCell className="py-1.5 text-xs">{r.pending}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div>
                  <p className="text-xs font-medium text-foreground mb-1">DOT Insurance Requirements</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="h-8 text-xs">Type</TableHead>
                        <TableHead className="h-8 text-xs">Required</TableHead>
                        <TableHead className="h-8 text-xs">On File</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {company.dotAuthority.requirements.map((r) => (
                        <TableRow key={r.type}>
                          <TableCell className="py-1.5 text-xs">{r.type}</TableCell>
                          <TableCell className="py-1.5 text-xs">{r.required}</TableCell>
                          <TableCell className="py-1.5 text-xs">{r.onFile}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div>
                  <p className="text-xs font-medium text-foreground mb-1">DOT Active / Pending Insurance</p>
                  <LabelRow label="Policy / surety">{company.dotAuthority.activeInsurance.policySurety}</LabelRow>
                  <LabelRow label="Coverage from">{company.dotAuthority.activeInsurance.coverageFrom}</LabelRow>
                  <LabelRow label="Coverage to">{company.dotAuthority.activeInsurance.coverageTo}</LabelRow>
                  <LabelRow label="Insurance carrier">{company.dotAuthority.activeInsurance.carrier}</LabelRow>
                  <LabelRow label="Contact">{company.dotAuthority.activeInsurance.contact}</LabelRow>
                  <LabelRow label="Address">
                    {company.dotAuthority.activeInsurance.address.map((l, i) => (
                      <span key={i} className="block">{l}</span>
                    ))}
                  </LabelRow>
                  <LabelRow label="Phone">{company.dotAuthority.activeInsurance.phone}</LabelRow>
                  <LabelRow label="Fax">{company.dotAuthority.activeInsurance.fax}</LabelRow>
                  <LabelRow label="Effective date">{company.dotAuthority.activeInsurance.effectiveDate}</LabelRow>
                  <LabelRow label="Cancellation date">{company.dotAuthority.activeInsurance.cancellationDate}</LabelRow>
                </div>

                <SourceFooter source={company.dotAuthority.source} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <h2 className="text-sm font-semibold text-foreground">Company Background</h2>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <LabelRow label="Primary contact">{company.background.primaryContact}</LabelRow>
                <LabelRow label="Primary title">{company.background.primaryTitle}</LabelRow>
                <LabelRow label="Corporation type">{company.background.corporationType}</LabelRow>
                <LabelRow label="Phone">{company.background.phone}</LabelRow>
                <LabelRow label="Email">{company.background.email}</LabelRow>
                <LabelRow label="Address">
                  {company.background.address.map((l, i) => (
                    <span key={i} className="block">{l}</span>
                  ))}
                </LabelRow>
                <LabelRow label="Year founded">{company.background.yearFounded}</LabelRow>
                <LabelRow label="SCAC code">{company.background.scac}</LabelRow>
                <LabelRow label="Veteran owned">{company.background.veteranOwned}</LabelRow>
                <LabelRow label="Woman owned">{company.background.womanOwned}</LabelRow>
                <LabelRow label="Minority owned">{company.background.minorityOwned}</LabelRow>
                <LabelRow label="Certified NMSDC">{company.background.certifiedNMSDC}</LabelRow>
                <LabelRow label="Other locations">{company.background.otherLocations}</LabelRow>
                <SourceFooter source={company.background.source} />
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col gap-3">
            <Card>
              <CardHeader className="pb-2 pt-4 px-4">
                <h2 className="text-sm font-semibold text-foreground">Credit Profile</h2>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <p className="text-xs text-muted-foreground mb-1">Name on record</p>
                <p className="text-sm text-foreground font-medium mb-4">{company.name}</p>

                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-[32px] font-semibold text-foreground leading-none tabular-nums">
                    {company.creditScore}
                  </span>
                  <span className="text-xs text-muted-foreground">out of 100</span>
                </div>
                <Badge variant="secondary" className="mb-5">Days to pay: {company.daysToPay}</Badge>

                <p className="text-xs font-medium text-foreground mb-2">Credit score & Days to pay history</p>
                <ChartContainer config={chartConfig} className="aspect-[2/1] w-full">
                  <LineChart data={company.creditHistory}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
                    <XAxis dataKey="month" tick={{ fontSize: 9 }} interval={2} />
                    <YAxis yAxisId="score" domain={[80, 100]} tick={{ fontSize: 9 }} />
                    <YAxis yAxisId="dtp" orientation="right" domain={[10, 35]} tick={{ fontSize: 9 }} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line yAxisId="score" type="monotone" dataKey="score" stroke="var(--color-score)" strokeWidth={2} dot={false} />
                    <Line yAxisId="dtp" type="monotone" dataKey="dtp" stroke="var(--color-dtp)" strokeWidth={2} dot={false} strokeDasharray="4 2" />
                  </LineChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
